<script setup lang="ts">
import { useSubscriptionStore } from '@/stores/subscription'
import { useAuth0 } from '@auth0/auth0-vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { computed, getCurrentInstance } from 'vue'
import { useHelpDocsQuery } from '@/composables/api/helpDocs'
import ClinicianSearchBar from '@/components/cliniciansViewer/ClinicianSearchBar.vue'

const currentRoute = useRoute()
const router = useRouter()
const self = getCurrentInstance()
const {
  isAuthenticated: isAuth,
  loginWithRedirect: login,
  logout: logoutWithRedirect,
  user
} = useAuth0()
const { t } = useI18n()
const subscriptionStore = useSubscriptionStore()
const { data, isSuccess } = useHelpDocsQuery()
const logout = () =>
  logoutWithRedirect({
    logoutParams: {
      returnTo: window.location.origin
    }
  })
const logoAlt = computed(() => {
  return `Logo for ${subscriptionStore.getCurrentSubscriberGroup.name}`
})

const openDialog = () => {
  if (self && isSuccess.value === true) {
    self.appContext.app.config.globalProperties.$buefy.dialog.confirm({
      message: t('download-user-guide'),
      type: 'is-primary',
      onConfirm: () => {
        window.open(data.value, '_blank')
      }
    })
  }
}
const openSettings = () => {
  router.push({ name: 'settings' })
}
const userPicture = computed(() => {
  const currentUser = user.value
  if (currentUser && 'picture' in currentUser) {
    return currentUser.picture
  }
  return undefined
})
</script>

<template>
  <b-navbar transparent>
    <template #brand>
      <b-navbar-item tag="router-link" :to="{ name: 'home' }">
        <img src="/img/Logo.svg" alt="Axuall" class="brand ml-1" />
      </b-navbar-item>
    </template>
    <template #start>
      <b-navbar-item
        tag="router-link"
        :active="currentRoute.name === 'home'"
        :to="{ name: 'home' }"
        aria-label="home"
      >
        {{ t('home') }}
      </b-navbar-item>
      <b-navbar-dropdown :label="$t('find-candidates')">
        <b-navbar-item
          tag="router-link"
          :active="currentRoute.name === 'job-welcome'"
          :to="{ name: 'job-welcome' }"
          aria-label="new-job"
        >
          {{ $t('new-job') }}
        </b-navbar-item>
        <b-navbar-item
          tag="router-link"
          :active="currentRoute.name === 'we-jobs'"
          :to="{ name: 'we-jobs' }"
          aria-label="saved-jobs"
        >
          {{ $t('saved-jobs') }}
        </b-navbar-item>
      </b-navbar-dropdown>
      <b-navbar-dropdown :label="$t('advanced-search-nav')">
        <b-navbar-item
          tag="router-link"
          :active="currentRoute.name === 'clinician-dashboard'"
          :to="{ name: 'clinician-dashboard', hash: '#clinicians' }"
          aria-label="clinicians"
        >
          {{ $t('new-search') }}
        </b-navbar-item>
        <b-navbar-item
          tag="router-link"
          :active="currentRoute.name === 'saved-searches'"
          :to="{ name: 'saved-searches' }"
          aria-label="saved-searches"
        >
          {{ $t('saved-searches') }}
        </b-navbar-item>
      </b-navbar-dropdown>
    </template>

    <template #end>
      <div class="column is-8-desktop is-9-tablet">
        <div>
          <clinician-search-bar />
        </div>
      </div>
      <b-navbar-dropdown class="top-right-nav-dropdown" right>
        <template #label>
          <span v-if="userPicture" id="profile-picture"
            ><b-image :src="userPicture" rounded custom-class="is-hidden-touch"
          /></span>
        </template>
        <b-navbar-item v-if="isAuth && subscriptionStore.getCurrentSubscriberGroup" tag="div">
          <img :alt="logoAlt" :src="subscriptionStore.getCurrentSubscriberGroup.settings.logo" />
        </b-navbar-item>
        <b-navbar-item v-if="!isAuth" @click="login">
          {{ $t('login') }}
        </b-navbar-item>
        <b-navbar-item v-if="isAuth" @click="openSettings">
          {{ $t('settings.navigation') }}
        </b-navbar-item>
        <b-navbar-item v-if="isAuth && isSuccess" @click="openDialog">
          {{ $t('help') }}
        </b-navbar-item>
        <b-navbar-item v-if="isAuth" @click="logout">
          {{ $t('logout') }}
        </b-navbar-item>
      </b-navbar-dropdown>
    </template>
  </b-navbar>
</template>

<style scoped lang="scss">
span#profile-picture {
  height: 28px;
  width: 28px;
}
:global(.top-right-nav-dropdown::after) {
  border-color: #fff !important;
}

nav.navbar
  :deep(
    div.navbar-menu div.navbar-start .navbar-item.has-dropdown div.navbar-dropdown a.navbar-item
  ) {
  color: #363143;
  @media screen and (max-width: 1024px) {
    color: #ebfffc;
  }
}

:deep(div.navbar-item > a.navbar-link::after) {
  border-color: #ebfffc;
}
</style>
